import { useEffect, useState } from "react";

const SEARCH_API_URL = 'https://recherche-entreprises.api.gouv.fr/search';

/**
 * hook that fetches, from the official "Recherche d'entreprises" API (api.gouv.fr),
 * the organization associated with a SIRET.
 * Pass null/undefined to skip the lookup (e.g. while the SIRET is being edited or is invalid).
 *
 * @param {string|null} siret SIRET number (already validated)
 * @returns {{ isLoading: boolean, organization: object|null, error: string|null }}
 *   organization: { name, establishmentName, city, isClosed }
 */
const useSiretOrganization = (siret) => {
    const [state, setState] = useState({ isLoading: false, organization: null, error: null });

    useEffect(() => {
        const digits = siret?.replace(/\s/g, '');
        if (!digits) {
            setState({ isLoading: false, organization: null, error: null });
            return;
        }

        // Ignore responses for a SIRET that is no longer the current one
        let ignore = false;
        setState({ isLoading: true, organization: null, error: null });

        fetch(`${SEARCH_API_URL}?q=${digits}&page=1&per_page=1`)
            .then(res => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.json();
            })
            .then(data => {
                if (ignore) return;
                const result = data?.results?.[0];
                const establishment = result?.matching_etablissements?.find(e => e.siret === digits)
                    ?? (result?.siege?.siret === digits ? result.siege : null);
                if (!result || !establishment) {
                    setState({ isLoading: false, organization: null, error: "Pas d'établissement trouvé" });
                    return;
                }
                const establishmentName = establishment.nom_commercial ?? establishment.liste_enseignes?.[0] ?? null;
                setState({
                    isLoading: false,
                    organization: {
                        name: result.nom_complet,
                        establishmentName: establishmentName && establishmentName !== result.nom_complet ? establishmentName : null,
                        city: establishment.libelle_commune,
                        isClosed: establishment.etat_administratif === 'F',
                    },
                    error: null,
                });
            })
            .catch(() => {
                if (ignore) return;
                setState({ isLoading: false, organization: null, error: "Erreur d'appel API gouv" });
            });

        return () => { ignore = true; };
    }, [siret]);

    return state;
};

export default useSiretOrganization;
